import { JSX } from "react";
import type { CellStatus, GuessCell } from "@/types/puzzle";

export type { CellStatus, GuessCell } from "@/types/puzzle";

export const g = (digit: string, status: CellStatus): GuessCell => ({ digit, status });

const DIGIT_COLORS: Record<CellStatus, string> = {
    correct: "tw-bg-violet-600",
    close: "tw-bg-orange-500",
    wrong: "tw-bg-gray-600",
    empty: "tw-bg-white",
};

export function GuessGrid({ guesses, compact = false }: { guesses: GuessCell[][]; compact?: boolean }) {
    const cellSize = compact ? "tw-w-4 tw-h-4" : "tw-w-6 tw-h-6";
    const gap = compact ? "tw-gap-0.5" : "tw-gap-1";
    const fontSize = compact ? "tw-text-xs" : "tw-text-sm";

    return (
        <div className={`tw-flex tw-flex-col ${gap}`}>
            {guesses.map((row, i) => (
                <div key={i} className={`tw-flex ${gap}`}>
                    {row.map((cell, j) => (
                        <div
                            key={j}
                            className={`tw-flex tw-items-center tw-justify-center tw-text-white tw-rounded tw-font-bold ${cellSize} ${fontSize} ${DIGIT_COLORS[cell.status]}`}
                        >
                            {cell.digit}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

export function ScoreBadge({
    guesses,
    expanded,
    onToggle,
    compact = false,
    isSolved = true,
}: {
    guesses: GuessCell[][];
    expanded: boolean;
    onToggle: () => void;
    compact?: boolean;
    isSolved?: boolean;
}) {
    const scoreLabel = isSolved ? `${guesses.length} guess${guesses.length !== 1 ? "es" : ""}` : "X";
    const containerPadding = "tw-px-2 tw-py-1";
    const dotSize = compact ? "tw-w-1 tw-h-1" : "tw-w-1.5 tw-h-1.5";
    const dotGap = compact ? "tw-gap-0.5" : "tw-gap-1";

    return (
        <div className="tw-inline-flex tw-flex-col">
            <button
                onClick={onToggle}
                className={`tw-inline-flex tw-items-center tw-cursor-pointer tw-transition-all tw-gap-1.5 tw-rounded-lg tw-border ${
                    expanded ? "tw-bg-violet-600/15 tw-border-violet-600" : "tw-bg-violet-600/8 tw-border-violet-600/20"
                } ${containerPadding}`}
            >
                <div className={`tw-flex tw-flex-col ${dotGap}`}>
                    {guesses.slice(0, 2).map((row, i) => (
                        <div key={i} className={`tw-flex ${dotGap}`}>
                            {row.map((cell, j) => (
                                <div key={j} className={`tw-rounded-sm ${dotSize} ${DIGIT_COLORS[cell.status]}`} />
                            ))}
                        </div>
                    ))}
                </div>
                <span
                    className={`tw-font-semibold tw-text-violet-600 tw-whitespace-nowrap ${
                        compact ? "tw-text-xs" : "tw-text-sm"
                    }`}
                >
                    {scoreLabel}
                </span>
            </button>

            {expanded && (
                <div className="tw-mt-2 tw-inline-block tw-p-2.5 tw-bg-violet-600/6 tw-rounded-lg tw-border tw-border-violet-600/12">
                    <GuessGrid guesses={guesses} compact={compact} />
                </div>
            )}
        </div>
    );
}

// Full size guess input grid for the puzzle
export function PuzzleGuessGrid({
    guesses,
    currentGuess,
    maxGuesses = 6,
}: {
    guesses: GuessCell[][];
    currentGuess: string[];
    maxGuesses?: number;
}) {
    const totalRows = maxGuesses;
    const cols = 8; // $X,XXX,XXX format positions

    const rows: JSX.Element[] = [];
    for (let r = 0; r < totalRows; r++) {
        const isCompletedRow = r < guesses.length;
        const isCurrentRow = r === guesses.length;
        const cells: JSX.Element[] = [];

        // positions: $ digit digit , digit digit digit , digit digit digit
        // Simplified: 8 digit positions with comma separators at position 2 and 5
        for (let c = 0; c < cols; c++) {
            const isComma = c === 2 || c === 5;
            if (isComma) {
                cells.push(
                    <span
                        key={`comma-${c}`}
                        className="tw-text-white tw-text-4xl tw-leading-[54px] tw-w-2.5 tw-text-center"
                    >
                        ,
                    </span>,
                );
            }

            let cellClasses = "tw-bg-white tw-border-gray-300 tw-text-gray-900";
            let digit = "";

            if (isCompletedRow) {
                const cell = guesses[r][c];
                digit = cell.digit;
                if (cell.status === "correct") {
                    cellClasses = "tw-bg-violet-600 tw-text-white";
                } else if (cell.status === "close") {
                    cellClasses = "tw-bg-orange-500 tw-text-white";
                } else {
                    cellClasses = "tw-bg-white tw-text-gray-900";
                }
            } else if (isCurrentRow) {
                digit = currentGuess[c] || "";
            }

            cells.push(
                <div
                    key={c}
                    className={`tw-flex tw-items-center tw-justify-center tw-rounded-sm tw-w-9 tw-h-9 tw-border tw-text-lg tw-font-medium ${cellClasses}`}
                >
                    {digit}
                </div>,
            );
        }

        rows.push(
            <div key={r} className="tw-flex tw-items-center tw-gap-1.5">
                {cells}
            </div>,
        );
    }

    return <div className="tw-flex tw-flex-col tw-gap-2">{rows}</div>;
}
